import React, { FC, Fragment } from 'react';
import { Box, Divider, TableCell, TableRow, Typography } from '@mui/material';
import { Fee, TransportLine, TransportOrder, TransportServices } from 'models/transport';
import { useTranslation } from 'react-i18next';
import TransportScheduleCell from './transport-schedule-cell.component';
import { transportProviderName } from 'utils/transport-provider-name';
import { NShiftShipmentWaybill } from 'models/nshift-shipment-waybill';

type TransportScheduleItemProps = {
    transportLine: TransportLine;
    onTransportClick: (transportOrder?: TransportOrder, waybills?: NShiftShipmentWaybill[]) => void;
};

const TransportScheduleItem: FC<TransportScheduleItemProps> = ({
    transportLine,
    onTransportClick,
}: TransportScheduleItemProps) => {
    const { t: translate } = useTranslation(['common', 'transport', 'transport-providers']);
    const pickups: TransportOrder[] = [];
    const transportDeliveries: TransportOrder[] = [];
    const nShiftDeliveries: NShiftShipmentWaybill[] = [];
    (transportLine.transportOrders || []).forEach((transportOrder) => {
        if (transportOrder.serviceCode === TransportServices.PICKUP) {
            pickups.push(transportOrder);
        } else {
            transportDeliveries.push(transportOrder);
        }
    });
    (transportLine.waybills || []).forEach((waybill) => {
        nShiftDeliveries.push(waybill);
    });
    const totalFee = transportLine.fee.reduce((total, fee: Fee) => {
        if (fee.value > 0) {
            total += parseFloat(fee.value?.toString() || '0');
        }
        return total;
    }, 0);

    const getTranslatedFeeName = (feeName: string) => {
        switch (feeName) {
            case '!tollFee':
                return translate('transport:TOLL_FEE');
            case '!eveningDeliveryFee':
                return translate('transport:EVENING_DELIVERY_FEE');
            case '!dayDeliveryFee':
                return translate('transport:DAY_DELIVERY_FEE');
            default:
                return feeName;
        }
    };

    const printFormattedFees = (transportLine: TransportLine) => {
        const fees = transportLine.fee.filter((fee: Fee) => {
            return fee.value > 0;
        });

        if (totalFee === 0) {
            return '0 kr';
        } else if (fees.length === 1) {
            return `${fees[0].value} kr`;
        }

        return fees.map((fee) => fee.value).join('kr + ') + ` kr = ${totalFee} kr`;
    };

    const transportProvider = (transportLine: TransportLine) => {
        if (transportLine.transportOrders && transportLine.transportOrders.length > 0) {
            return translate(transportProviderName(transportLine.transportOrders[0].transportProvider));
        }
        if (transportLine.waybills && transportLine.waybills.length > 0) {
            return translate(`transport-providers:${transportLine.transportProviderName}`);
        }
        return '';
    };

    return (
        <TableRow hover style={transportLine.isDeleted ? { textDecoration: 'line-through', color: 'red' } : {}}>
            <TableCell>
                <Typography variant="body1">{getTranslatedFeeName(transportLine.name)}</Typography>
            </TableCell>
            <TableCell>
                {pickups.length
                    ? pickups.map((transportOrder, index) => (
                          <Fragment key={index}>
                              <TransportScheduleCell
                                  transportOrder={transportOrder}
                                  onClick={() => onTransportClick(transportOrder)}
                              />
                              {index !== pickups.length - 1 ? (
                                  <Box my={1}>
                                      <Divider sx={{ width: 210, borderColor: '#ddd' }} />
                                  </Box>
                              ) : undefined}
                          </Fragment>
                      ))
                    : undefined}
            </TableCell>
            <TableCell>
                {transportDeliveries.length
                    ? transportDeliveries.map((transportOrder, index) => (
                          <Fragment key={index}>
                              <TransportScheduleCell
                                  transportOrder={transportOrder}
                                  onClick={() => onTransportClick(transportOrder)}
                              />
                              {index !== transportDeliveries.length - 1 ? (
                                  <Box my={1}>
                                      <Divider sx={{ width: 210, borderColor: '#ddd' }} />
                                  </Box>
                              ) : undefined}
                          </Fragment>
                      ))
                    : undefined}
                {nShiftDeliveries.length ? (
                    <Fragment>
                        <TransportScheduleCell
                            waybills={nShiftDeliveries}
                            onClick={() => onTransportClick(undefined, nShiftDeliveries)}
                        />
                    </Fragment>
                ) : undefined}
            </TableCell>
            <TableCell>
                <Typography>{printFormattedFees(transportLine)}</Typography>
            </TableCell>
            <TableCell>
                <Typography variant="caption">{transportProvider(transportLine)}</Typography>
            </TableCell>
        </TableRow>
    );
};

export default TransportScheduleItem;
